import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Wallet,
  TrendingDown,
  ArrowDownLeft,
  ArrowUpRight,
  Calendar,
  Download,
  RefreshCw,
  DollarSign,
  AlertCircle,
  ClipboardList,
  BarChart3,
} from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../contexts/AuthContext';
import { api, ReportFilters, CashflowTransaction } from '../../lib/api';
import { LoadingState } from '../../components/ui/Spinner';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DailyActivityReport } from '../reports';

type DateRange = 'today' | 'week' | 'month' | 'custom';
type ViewMode = 'overview' | 'daily';
type EmployeeScope = 'own' | 'all';

export default function CashierReportsPage() {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [cashflowPage, setCashflowPage] = useState(1);
  const cashflowLimit = 25;
  const [employeeScope, setEmployeeScope] = useState<EmployeeScope>('own');

  // Format date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString)
  const toLocalDateString = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Build date filters
  const getDateFilters = (): ReportFilters => {
    const today = new Date();
    const filters: ReportFilters = {};

    if (dateRange === 'today') {
      filters.startDate = toLocalDateString(today);
      filters.endDate = toLocalDateString(today);
    } else if (dateRange === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filters.startDate = toLocalDateString(weekAgo);
      filters.endDate = toLocalDateString(today);
    } else if (dateRange === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      filters.startDate = toLocalDateString(monthAgo);
      filters.endDate = toLocalDateString(today);
    } else if (dateRange === 'custom' && customStartDate && customEndDate) {
      filters.startDate = customStartDate;
      filters.endDate = customEndDate;
    }

    return filters;
  };

  const dateFilters = getDateFilters();
  const includeAllEmployees = employeeScope === 'all';

  // Fetch Balances
  const { data: balancesData, isLoading: balancesLoading, refetch: refetchBalances } = useQuery({
    queryKey: ['reports', 'balances', currentEntity?.id],
    queryFn: () => api.reports.balances(currentEntity!.id),
    enabled: !!currentEntity,
  });

  // Fetch Daily Summary
  const { data: dailySummaryData, isLoading: dailySummaryLoading, refetch: refetchDailySummary } = useQuery({
    queryKey: ['reports', 'daily-summary', currentEntity?.id, dateFilters, includeAllEmployees],
    queryFn: () => api.reports.dailySummary(currentEntity!.id, {
      ...dateFilters,
      includeAllEmployees,
    }),
    enabled: !!currentEntity,
  });

  // Fetch Cashflow Detailed
  const { data: cashflowData, isLoading: cashflowLoading, refetch: refetchCashflow } = useQuery({
    queryKey: ['reports', 'cashflow-detailed', currentEntity?.id, dateFilters, includeAllEmployees, cashflowPage, cashflowLimit],
    queryFn: () =>
      api.reports.cashflowDetailed(currentEntity!.id, {
        ...dateFilters,
        includeAllEmployees,
        page: cashflowPage,
        limit: cashflowLimit,
      }),
    enabled: !!currentEntity,
  });

  // Reset page when date filters change
  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    setCashflowPage(1);
  };

  // Note: Profit data comes from dailySummary for cashier role
  // The separate /api/reports/profit endpoint requires admin permissions

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchBalances(),
        refetchDailySummary(),
        refetchCashflow(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Small delay to show loading state
      await new Promise((resolve) => setTimeout(resolve, 300));

      const sections: string[] = [];
      const dateRangeLabel = dateRange === 'custom'
        ? `${customStartDate} to ${customEndDate}`
        : dateRange;

      // Header
      sections.push(`Financial Report - ${currentEntity?.name || 'Entity'}`);
      sections.push(`Generated: ${new Date().toLocaleString()}`);
      sections.push(`Date Range: ${dateRangeLabel}`);
      sections.push('');

      // Wallet Balances
      if (balancesData?.totals?.length) {
        sections.push('=== WALLET BALANCES ===');
        sections.push('Currency,Balance');
        balancesData.totals.forEach((item) => {
          sections.push(`${item.currency},${formatAmount(item.amount || 0)}`);
        });
        sections.push('');
      }

      // Incoming
      if (dailySummaryData?.incoming?.length) {
        sections.push('=== INCOMING ===');
        sections.push('Currency,Count,Total');
        dailySummaryData.incoming.forEach((item) => {
          sections.push(`${item.currency},${item.count},${formatAmount(item.total)}`);
        });
        sections.push('');
      }

      // Outgoing
      if (dailySummaryData?.outgoing?.length) {
        sections.push('=== OUTGOING ===');
        sections.push('Currency,Count,Total');
        dailySummaryData.outgoing.forEach((item) => {
          sections.push(`${item.currency},${item.count},${formatAmount(item.total)}`);
        });
        sections.push('');
      }

      // Open Debts
      if (dailySummaryData?.openDebts?.length) {
        sections.push('=== OPEN DEBTS ===');
        sections.push('Currency,Count,Total');
        dailySummaryData.openDebts.forEach((item) => {
          sections.push(`${item.currency},${item.count},${formatAmount(item.total)}`);
        });
        sections.push('');
      }

      // Cashflow - Detailed Transactions
      if (cashflowData?.data?.length) {
        sections.push('=== CASHFLOW TRANSACTIONS ===');
        sections.push('Transaction ID,Customer,Item,Description,Date Trade In,Amount In,In Currency,Amount Out,Out Currency,Date Trade Out,Payment Status,Debt Balance,Employee');
        cashflowData.data.forEach((tx: CashflowTransaction) => {
          const dateIn = new Date(tx.transactionDate || tx.createdAt).toLocaleDateString('en-GB');
          const dateOut = tx.updatedAt && tx.status === 'closed' ? new Date(tx.updatedAt).toLocaleDateString('en-GB') : '';
          const amountIn = tx.amountIn || (tx.amount && tx.type === 'deposit' ? tx.amount : '');
          const currencyIn = tx.currencyIn || (tx.currency && tx.type === 'deposit' ? tx.currency : '');
          const amountOut = tx.amountOut || (tx.amount && (tx.type === 'withdrawal' || tx.type === 'expense') ? tx.amount : '');
          const currencyOut = tx.currencyOut || (tx.currency && (tx.type === 'withdrawal' || tx.type === 'expense') ? tx.currency : '');
          const paymentStatus = tx.status === 'closed' ? 'Close Trade' : 'Open Trade';
          const debtBalance = tx.debtAmount && parseFloat(tx.debtAmount) > 0 ? tx.debtAmount : '';
          const description = tx.comment ? `"${tx.comment.replace(/"/g, '""')}"` : '';
          sections.push(`${tx.id.slice(0, 8)},${tx.clientName || tx.clientUniqueId || ''},${tx.type},${description},${dateIn},${amountIn ? formatAmount(amountIn) : ''},${currencyIn},${amountOut ? formatAmount(amountOut) : ''},${currencyOut},${dateOut},${paymentStatus},${debtBalance ? formatAmount(debtBalance) : ''},${tx.employeeName || ''}`);
        });
        sections.push('');
      }

      // Transaction Counts
      if (dailySummaryData?.transactionCounts && Object.keys(dailySummaryData.transactionCounts).length > 0) {
        sections.push('=== TRANSACTION COUNTS ===');
        sections.push('Type,Count');
        Object.entries(dailySummaryData.transactionCounts).forEach(([type, count]) => {
          sections.push(`${type},${count}`);
        });
      }

      const csvContent = sections.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${currentEntity?.name || 'export'}-${dateRangeLabel.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  if (!currentEntity) return null;

  const formatAmount = (amount: number | string, decimals = 2) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const isLoading = balancesLoading || dailySummaryLoading || cashflowLoading;

  // Currency color mapping for balance cards
  const getCurrencyColor = (currency: string, index: number) => {
    const currencyColors: Record<string, { bg: string; text: string; border: string }> = {
      USD: { bg: 'bg-ctp-green/15', text: 'text-ctp-green', border: 'border-ctp-green/30' },
      USDT: { bg: 'bg-ctp-teal/15', text: 'text-ctp-teal', border: 'border-ctp-teal/30' },
      USDC: { bg: 'bg-ctp-teal/15', text: 'text-ctp-teal', border: 'border-ctp-teal/30' },
      EUR: { bg: 'bg-ctp-blue/15', text: 'text-ctp-blue', border: 'border-ctp-blue/30' },
      RUB: { bg: 'bg-ctp-red/15', text: 'text-ctp-red', border: 'border-ctp-red/30' },
      BTC: { bg: 'bg-ctp-peach/15', text: 'text-ctp-peach', border: 'border-ctp-peach/30' },
      ETH: { bg: 'bg-ctp-mauve/15', text: 'text-ctp-mauve', border: 'border-ctp-mauve/30' },
    };
    const defaultColors = [
      { bg: 'bg-ctp-blue/15', text: 'text-ctp-blue', border: 'border-ctp-blue/30' },
      { bg: 'bg-ctp-green/15', text: 'text-ctp-green', border: 'border-ctp-green/30' },
      { bg: 'bg-ctp-peach/15', text: 'text-ctp-peach', border: 'border-ctp-peach/30' },
      { bg: 'bg-ctp-mauve/15', text: 'text-ctp-mauve', border: 'border-ctp-mauve/30' },
      { bg: 'bg-ctp-teal/15', text: 'text-ctp-teal', border: 'border-ctp-teal/30' },
      { bg: 'bg-ctp-yellow/15', text: 'text-ctp-yellow', border: 'border-ctp-yellow/30' },
    ];
    return currencyColors[currency.toUpperCase()] || defaultColors[index % defaultColors.length];
  };



  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-[1450px] mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-center flex-col md:flex-row gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-bold text-foreground m-0">{t('cashier.reports.title', 'Reports')}</h1>
            <p className="text-sm text-foreground-muted m-0">
              {t('cashier.reports.subtitle', 'Financial overview for')} {user?.fullName || 'Cashier'}
            </p>
          </div>
          <div className="flex gap-3">
            {viewMode === 'overview' && (
              <>
                <button
                  className="flex items-center gap-2 px-4 py-2.5 bg-surface text-foreground border border-border rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleRefreshAll}
                  disabled={isRefreshing}
                >
                  <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
                  {isRefreshing ? t('common.refreshing', 'Refreshing...') : t('cashier.reports.refresh', 'Refresh')}
                </button>
                <button
                  className="flex items-center gap-2 px-4 py-2.5 bg-surface text-foreground border border-border rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleExport}
                  disabled={isExporting || isLoading}
                >
                  <Download size={18} className={isExporting ? 'animate-pulse' : ''} />
                  {isExporting ? t('common.exporting', 'Exporting...') : t('cashier.reports.export', 'Export')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* View Mode Tabs + Employee Scope Toggle */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="inline-flex bg-surface rounded-lg p-1 border border-border shadow-sm">
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${viewMode === 'overview' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
              onClick={() => setViewMode('overview')}
            >
              <BarChart3 size={16} />
              {t('cashier.reports.overview', 'Overview')}
            </button>
            <button
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${viewMode === 'daily' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
              onClick={() => setViewMode('daily')}
            >
              <ClipboardList size={16} />
              {t('cashier.reports.dailyActivity', 'Daily Activity')}
            </button>
          </div>

          {viewMode === 'overview' && (
            <div className="inline-flex bg-surface rounded-lg p-1 border border-border shadow-sm">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${employeeScope === 'own' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
                onClick={() => setEmployeeScope('own')}
              >
                {t('cashier.reports.scope.own', 'My transactions')}
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${employeeScope === 'all' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
                onClick={() => setEmployeeScope('all')}
              >
                {t('cashier.reports.scope.all', 'All users')}
              </button>
            </div>
          )}
        </div>

        {viewMode === 'daily' ? (
          <DailyActivityReport entityId={currentEntity!.id} />
        ) : (
          <>
        {/* Date Range Filters */}
        <div className="flex gap-4 flex-wrap items-center">
          <div className="inline-flex bg-surface rounded-lg p-1 border border-border shadow-sm">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${dateRange === 'today' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
              onClick={() => handleDateRangeChange('today')}
            >
              {t('cashier.reports.today', 'Today')}
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${dateRange === 'week' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
              onClick={() => handleDateRangeChange('week')}
            >
              {t('cashier.reports.week', 'This Week')}
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${dateRange === 'month' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
              onClick={() => handleDateRangeChange('month')}
            >
              {t('cashier.reports.month', 'This Month')}
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-all ${dateRange === 'custom' ? 'bg-ctp-blue text-white shadow-sm' : 'text-foreground-muted hover:text-foreground'}`}
              onClick={() => handleDateRangeChange('custom')}
            >
              {t('cashier.reports.custom', 'Custom')}
            </button>
          </div>

          {dateRange === 'custom' && (
            <div className="flex gap-2 items-center">
              <div className="relative">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="pl-10 pr-4 py-2.5 bg-surface border border-border rounded-lg text-sm text-foreground cursor-pointer outline-none focus:border-ctp-blue"
                />
              </div>
              <span className="text-foreground-muted">to</span>
              <div className="relative">
                <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="pl-10 pr-4 py-2.5 bg-surface border border-border rounded-lg text-sm text-foreground cursor-pointer outline-none focus:border-ctp-blue"
                />
              </div>
            </div>
          )}
        </div>

        {/* Wallet Balances Section — individual wallet cards */}
        <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
            <Wallet size={20} className="text-ctp-blue" />
            <h2 className="text-base font-semibold text-foreground m-0">{t('cashier.reports.walletBalances', 'Wallet Balances')}</h2>
          </div>
          {balancesLoading ? (
            <div className="py-12 flex items-center justify-center">
              <LoadingState message={t('common.loading', 'Loading...')} />
            </div>
          ) : (
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {balancesData?.byType && Object.keys(balancesData.byType).length > 0 ? (
                  Object.entries(balancesData.byType).map(([_type, wallets]) =>
                    (wallets as any[]).map((w: any) => {
                      const colors = getCurrencyColor(w.currency, 0);
                      const balance = parseFloat(w.balance || '0');
                      return (
                        <div key={w.walletId} className="rounded-xl border border-border bg-surface p-4 flex flex-col gap-2 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-foreground">{w.walletName}</span>
                              <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>{w.currency}</span>
                            </div>
                            <div className="text-right">
                              <div className="text-[10px] uppercase tracking-wide text-foreground-muted">{t('cashier.reports.balanceLabel', 'Balance')}</div>
                              <div className={`text-lg font-bold ${balance >= 0 ? 'text-foreground' : 'text-ctp-red'}`}>
                                {formatAmount(balance)} <span className="text-xs font-normal text-foreground-muted">{w.currency}</span>
                              </div>
                            </div>
                          </div>
                          <Link
                            to={`/movements?walletId=${w.walletId}&walletName=${encodeURIComponent(w.walletName)}`}
                            className="flex items-center justify-center gap-1.5 mt-1 px-3 py-1.5 text-xs font-medium text-ctp-blue bg-ctp-blue/10 rounded-lg border border-ctp-blue/20 hover:bg-ctp-blue/20 transition-colors no-underline"
                          >
                            <ArrowRight size={12} />
                            {t('cashier.reports.viewMovements', 'Movements')}
                          </Link>
                        </div>
                      );
                    })
                  )
                ) : (
                  <div className="col-span-full text-center text-foreground-muted py-4">
                    {t('cashier.reports.noBalances', 'No balance data available')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Daily Summary Section - 3 Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Incoming */}
          <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <ArrowDownLeft size={20} className="text-ctp-green" />
              <h2 className="text-base font-semibold text-foreground m-0">{t('cashier.reports.incoming', 'Incoming')}</h2>
            </div>
            {dailySummaryLoading ? (
              <div className="py-12 flex items-center justify-center">
                <LoadingState message={t('common.loading', 'Loading...')} />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.currency', 'Currency')}</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.count', '#')}</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.total', 'Total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailySummaryData?.incoming?.length ? (
                      dailySummaryData.incoming.map((item) => (
                        <tr key={item.currency} className="border-b border-border last:border-0">
                          <td className="px-4 py-2.5 font-medium text-foreground">{item.currency}</td>
                          <td className="px-4 py-2.5 text-right text-foreground-muted">{item.count}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-ctp-green">+{formatAmount(item.total)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="px-4 py-6 text-center text-foreground-muted text-sm">
                          {t('cashier.reports.noIncoming', 'No incoming')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Outgoing */}
          <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <ArrowUpRight size={20} className="text-ctp-red" />
              <h2 className="text-base font-semibold text-foreground m-0">{t('cashier.reports.outgoing', 'Outgoing')}</h2>
            </div>
            {dailySummaryLoading ? (
              <div className="py-12 flex items-center justify-center">
                <LoadingState message={t('common.loading', 'Loading...')} />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.currency', 'Currency')}</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.count', '#')}</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.total', 'Total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailySummaryData?.outgoing?.length ? (
                      dailySummaryData.outgoing.map((item) => (
                        <tr key={item.currency} className="border-b border-border last:border-0">
                          <td className="px-4 py-2.5 font-medium text-foreground">{item.currency}</td>
                          <td className="px-4 py-2.5 text-right text-foreground-muted">{item.count}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-ctp-red">-{formatAmount(item.total)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="px-4 py-6 text-center text-foreground-muted text-sm">
                          {t('cashier.reports.noOutgoing', 'No outgoing')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>

        {/* Open Debts & Transaction Counts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Open Debts */}
          <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <AlertCircle size={20} className="text-ctp-peach" />
              <h2 className="text-base font-semibold text-foreground m-0">{t('cashier.reports.openDebts', 'Open Debts')}</h2>
            </div>
            {dailySummaryLoading ? (
              <div className="py-12 flex items-center justify-center">
                <LoadingState message={t('common.loading', 'Loading...')} />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-5 py-3 text-left text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.currency', 'Currency')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.count', 'Count')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-foreground-muted uppercase">{t('cashier.reports.total', 'Total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailySummaryData?.openDebts?.length ? (
                      dailySummaryData.openDebts.map((item) => (
                        <tr key={item.currency} className="border-b border-border last:border-0">
                          <td className="px-5 py-3 font-medium text-foreground">{item.currency}</td>
                          <td className="px-5 py-3 text-right text-foreground-muted">{item.count}</td>
                          <td className="px-5 py-3 text-right font-semibold text-ctp-peach">{formatAmount(item.total)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="px-5 py-8 text-center text-foreground-muted">
                          {t('cashier.reports.noDebts', 'No open debts')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Transaction Counts */}
          <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <TrendingDown size={20} className="text-ctp-mauve" />
              <h2 className="text-base font-semibold text-foreground m-0">{t('cashier.reports.transactionCounts', 'Transaction Counts')}</h2>
            </div>
            {dailySummaryLoading ? (
              <div className="py-12 flex items-center justify-center">
                <LoadingState message={t('common.loading', 'Loading...')} />
              </div>
            ) : (
              <div className="p-5">
                <div className="grid grid-cols-2 gap-3">
                  {dailySummaryData?.transactionCounts && Object.keys(dailySummaryData.transactionCounts).length > 0 ? (
                    Object.entries(dailySummaryData.transactionCounts).map(([type, count]) => (
                      <div key={type} className="bg-surface-hover rounded-lg p-3 border border-border">
                        <div className="text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-1">{type}</div>
                        <div className="text-xl font-bold text-foreground">{count as number}</div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center text-foreground-muted py-4">
                      {t('cashier.reports.noTransactions', 'No transactions')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cashflow Section - Detailed Transaction Table */}
        <div className="bg-surface rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <DollarSign size={20} className="text-ctp-blue" />
              <h2 className="text-base font-semibold text-foreground m-0">{t('cashier.reports.cashflow', 'Cashflow')}</h2>
            </div>
            {cashflowData?.meta && (
              <span className="text-sm text-foreground-muted">
                {t('cashier.reports.totalTransactions', 'Total')}: {cashflowData.meta.total}
              </span>
            )}
          </div>
          {cashflowLoading ? (
            <div className="py-12 flex items-center justify-center">
              <LoadingState message={t('common.loading', 'Loading...')} />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1400px]">
                  <thead>
                    <tr className="border-b border-border bg-surface-hover/50">
                      <th className="px-3 py-3 text-left text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.transactionId', 'Transaction ID')}</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.customer', 'Customer / Company')}</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.item', 'Item')}</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.description', 'Description')}</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.dateTradeIn', 'Date Trade In')}</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.amountIn', 'Amount In')}</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.currencyIn', 'In Currency')}</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.amountOut', 'Amount Out')}</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.currencyOut', 'Out Currency')}</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.dateTradeOut', 'Date Trade Out')}</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.paymentStatus', 'Payment')}</th>
                      <th className="px-3 py-3 text-right text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.debtBalance', 'Debt Balance')}</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold text-foreground-muted uppercase whitespace-nowrap">{t('cashier.reports.employee', 'Employee')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashflowData?.data?.length ? (
                      cashflowData.data.map((tx: CashflowTransaction) => {
                        const hasDebt = tx.debtAmount && parseFloat(tx.debtAmount) > 0;
                        const isClosed = tx.status === 'closed';
                        const rowBgClass = hasDebt
                          ? 'bg-ctp-peach/10'
                          : isClosed
                            ? 'bg-ctp-green/5'
                            : '';
                        return (
                          <tr key={tx.id} className={`border-b border-border last:border-0 hover:bg-surface-hover/50 ${rowBgClass}`}>
                            <td className="px-3 py-2.5 font-mono text-xs text-foreground">{tx.id.slice(0, 8)}</td>
                            <td className="px-3 py-2.5 text-sm text-foreground">{tx.clientName || tx.clientUniqueId || '-'}</td>
                            <td className="px-3 py-2.5 text-sm">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                tx.type === 'exchange' ? 'bg-ctp-blue/15 text-ctp-blue' :
                                tx.type === 'deposit' ? 'bg-ctp-green/15 text-ctp-green' :
                                tx.type === 'withdrawal' ? 'bg-ctp-red/15 text-ctp-red' :
                                tx.type === 'expense' ? 'bg-ctp-peach/15 text-ctp-peach' :
                                tx.type === 'transfer' ? 'bg-ctp-mauve/15 text-ctp-mauve' :
                                'bg-ctp-overlay0/15 text-foreground-muted'
                              }`}>
                                {tx.type === 'exchange' ? t('transactions.types.exchange', 'Foreign exchange') :
                                 tx.type === 'deposit' ? t('transactions.types.deposit', 'Deposit') :
                                 tx.type === 'withdrawal' ? t('transactions.types.withdrawal', 'Withdrawal') :
                                 tx.type === 'expense' ? t('transactions.types.expense', 'Operating expenses') :
                                 tx.type === 'transfer' ? t('transactions.types.transfer', 'Transfer') :
                                 tx.type}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-sm text-foreground-muted max-w-[200px] truncate" title={tx.comment || ''}>
                              {tx.comment || '-'}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-foreground whitespace-nowrap">
                              {new Date(tx.transactionDate || tx.createdAt).toLocaleDateString('en-GB')}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-right font-medium text-ctp-green">
                              {tx.amountIn ? formatAmount(tx.amountIn) : tx.amount && tx.type === 'deposit' ? formatAmount(tx.amount) : '-'}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-center font-medium text-foreground-muted">
                              {tx.currencyIn || (tx.currency && tx.type === 'deposit' ? tx.currency : '-')}
                              {tx.walletInName && <span className="block text-[10px] text-foreground-muted/70">({tx.walletInName})</span>}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-right font-medium text-ctp-red">
                              {tx.amountOut ? formatAmount(tx.amountOut) : tx.amount && (tx.type === 'withdrawal' || tx.type === 'expense') ? formatAmount(tx.amount) : '-'}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-center font-medium text-foreground-muted">
                              {tx.currencyOut || (tx.currency && (tx.type === 'withdrawal' || tx.type === 'expense') ? tx.currency : '-')}
                              {tx.walletOutName && <span className="block text-[10px] text-foreground-muted/70">({tx.walletOutName})</span>}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-foreground whitespace-nowrap">
                              {tx.updatedAt && isClosed ? new Date(tx.updatedAt).toLocaleDateString('en-GB') : '-'}
                            </td>
                            <td className="px-3 py-2.5 text-center">
                              <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                isClosed ? 'bg-ctp-green/15 text-ctp-green' : 'bg-ctp-peach/15 text-ctp-peach'
                              }`}>
                                {isClosed ? t('cashier.reports.closeTrade', 'Close Trade') : t('cashier.reports.openTrade', 'Open Trade')}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-sm text-right font-medium">
                              {hasDebt ? (
                                <span className="text-ctp-peach">{formatAmount(tx.debtAmount!)}</span>
                              ) : '-'}
                            </td>
                            <td className="px-3 py-2.5 text-sm text-foreground">{tx.employeeName || '-'}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={13} className="px-5 py-8 text-center text-foreground-muted">
                          {t('cashier.reports.noCashflow', 'No cashflow data available')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {cashflowData?.meta && cashflowData.meta.totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-surface-hover/30">
                  <span className="text-sm text-foreground-muted">
                    {t('cashier.reports.showingPage', 'Page')} {cashflowData.meta.page} {t('cashier.reports.of', 'of')} {cashflowData.meta.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-foreground bg-surface border border-border rounded-md hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setCashflowPage(p => Math.max(1, p - 1))}
                      disabled={cashflowPage <= 1}
                    >
                      <ChevronLeft size={16} />
                      {t('common.previous', 'Previous')}
                    </button>
                    <button
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-foreground bg-surface border border-border rounded-md hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setCashflowPage(p => Math.min(cashflowData.meta.totalPages, p + 1))}
                      disabled={cashflowPage >= cashflowData.meta.totalPages}
                    >
                      {t('common.next', 'Next')}
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
}
